export interface ISimpleChange<T> {
    firstChange: boolean;
    previousValue: T;
    currentValue: T;
    isFirstChange: () => boolean;
}

export type TCallBackFunction<T> = (value: T, change?: ISimpleChange<T>) => void;

/**
 * Watch property change.
 *
 * @usage
 * ```typescript
 * class PersonComponent {
 *   // optional "change" parameter
 *   @OnChange<string>((value, change: ISimpleChange) => {
 *     console.log(`name is changed from ${change.previousValue} to ${value}`);
 *   })
 *   name: string;
 * }
 * ```
 *
 * !WORNING
 * As callback use non arrow function.
 *
 * ```typescript
 * class PersonComponent {
 *   /// wrong
 *   @OnChange<string>((value, change: ISimpleChange) => {
 *     console.log(`name is changed from ${change.previousValue} to ${value}`);
 *     this.update(); // "this" would refer to undefined, cannot access "update" of undefined
 *   })
 *   name: string;
 *
 *   /// correct
 *   @OnChange<string>(function(value, change: ISimpleChange) {
 *     console.log(`name is changed from ${change.previousValue} to ${value}`);
 *     this.update(); // this will work
 *   })
 *   name: string;
 *
 *   /// correct
 *   @OnChange<string>('onNameChange')
 *   name: string;
 *
 *   onNameChange(value, change) {
 *     console.log(`name is changed from ${change.previousValue} to ${value}`);
 *   }
 * }
 * ```
 */
export function OnChange<T = any>(callback: string | TCallBackFunction<T>) {
    const cachedValueKey = Symbol();
    const isFirstChangeKey = Symbol();

    return (target: any, key: PropertyKey) => {
        const callBackFn: TCallBackFunction<T> = typeof callback === 'string' ? target[callback] : callback;

        if (!callBackFn) {
            throw new Error(`Cannot find method ${callback} in class ${target.constructor.name}`);
        }

        Object.defineProperty(target, key, {
            set(value) {
                // change status of "isFirstChange"
                this[isFirstChangeKey] = this[isFirstChangeKey] === undefined;

                // No operation if new value is same as old value
                if (!this[isFirstChangeKey] && this[cachedValueKey] === value) {
                    return;
                }

                const oldValue = this[cachedValueKey];
                this[cachedValueKey] = value;
                const simpleChange: ISimpleChange<T> = {
                    firstChange: this[isFirstChangeKey],
                    previousValue: oldValue,
                    currentValue: this[cachedValueKey],
                    isFirstChange: () => this[isFirstChangeKey]
                };
                callBackFn.call(this, this[cachedValueKey], simpleChange);
            },
            get() {
                return this[cachedValueKey];
            }
        });
    };
}
